/**
 * URL helpers for listings whose street address must not appear in the path (hidden / obfuscated).
 *
 * Canonical public paths look like:
 * `/{city}-real-estate/{neighbourhood}/listing-{code}`
 * or legacy two-segment `/{city}-real-estate/listing-{code}`.
 */

const LISTING_PREFIX = 'listing-';

/**
 * True when a path segment is a hidden-listing token (`listing-` + opaque code).
 */
export function isHiddenListingPathSegment(segment: string): boolean {
  if (!segment || segment.length <= LISTING_PREFIX.length) {
    return false;
  }
  return segment.startsWith(LISTING_PREFIX);
}

/**
 * True when the composite slug used by routing/visibility starts with `listing-`
 * (e.g. `listing-abc123-toronto-on`).
 */
export function isHiddenListingSlug(slug: string): boolean {
  return typeof slug === 'string' && slug.startsWith(LISTING_PREFIX);
}

/**
 * Returns the last path segment if it is a `listing-{code}` segment.
 */
export function extractHiddenListingSegmentFromPath(path: string): string | undefined {
  const segments = path.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  return last && isHiddenListingPathSegment(last) ? last : undefined;
}

/**
 * Extracts the opaque code from a `listing-{code}` segment (no prefix).
 */
export function hiddenListingCodeFromSegment(segment: string): string {
  return segment.slice(LISTING_PREFIX.length);
}

/**
 * Parses a listing id from the opaque code: plain integer, or JSON/base64 payloads used by legacy obfuscation.
 */
export function parseListingIdFromHiddenListingCode(code: string): number | undefined {
  const trimmed = code.trim();
  if (/^\d+$/.test(trimmed)) {
    const n = parseInt(trimmed, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  try {
    const decoded = decodeBase64Utf8(trimmed);
    if (!decoded) {
      return undefined;
    }
    const parsed: unknown = JSON.parse(decoded);
    if (parsed && typeof parsed === 'object' && 'id' in parsed) {
      const id = Number((parsed as { id: unknown }).id);
      return Number.isFinite(id) && id > 0 ? id : undefined;
    }
  } catch {
    // ignore invalid base64 / JSON
  }
  return undefined;
}

/**
 * True when the URL should be treated as a hidden-address listing route (nofollow, no map, etc.).
 * Accepts legacy base64-only codes and newer alphanumeric / slug-style codes.
 */
export function isHiddenAddressListingPublicPath(addressPath: string): boolean {
  return extractHiddenListingSegmentFromPath(addressPath) !== undefined;
}

/**
 * Builds the public URL path for a hidden-address listing when the API did not send {@link addressUrlAbsolutePath}.
 * Prefer server-provided paths whenever available.
 */
export function buildHiddenListingHref(listing: {
  id: number | string;
  subDivision: string;
  province: string;
  neighbourhood?: string;
  addressUrlAbsolutePath?: string;
  listingUrlAbsolutePath?: string;
}): string {
  const fromApi = listing.addressUrlAbsolutePath || listing.listingUrlAbsolutePath;
  if (typeof fromApi === 'string' && fromApi.length > 0) {
    return fromApi.startsWith('/') ? fromApi : `/${fromApi}`;
  }
  const citySlug = `${listing.subDivision.toLowerCase().replace(/\s+/g, '-')}-${listing.province.toLowerCase()}`;
  const neigh = listing.neighbourhood?.trim()
    ? listing.neighbourhood.toLowerCase().replace(/\s+/g, '-')
    : undefined;
  const listingSeg = `listing-${listing.id}`;
  if (neigh) {
    return `/${citySlug}-real-estate/${neigh}/${listingSeg}`;
  }
  return `/${citySlug}-real-estate/${listingSeg}`;
}

function decodeBase64Utf8(b64: string): string | undefined {
  try {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  } catch {
    return undefined;
  }
}
